<template>
  <g
    :data-space-id="space.id"
    :data-space-object-type="space.spaceObjectType"
  >
    <dd-disp-space
      v-if="!space.isDispOnlyDoorOpenLine"
      :space="space"
      :spacePath="spacePath"
      :colorDispTex="colorDispTex"
      :dxfFace="dxfFace"
      :addTargetSpaceId="addTargetSpace ? addTargetSpace.id : undefined"
      :editTargetCellSpaceParentId="editTargetCellSpaceParentId"
      :strokeWidth="strokeWidth"
      :viewType="viewSet.viewType"
      :colorDispTexSize="colorDispTexSize"
      :isDispProcess="isDispProcess"
      :ownProcessList="isDispProcess ? ownProcessList: []"
      :scaleStrokeWidth="scaleStrokeWidth"
      :pathSize="Object.keys(dxfFace).length ? pathSize : {}"
      :showInfillLine="showInfillLine(space)"
      :isDispAccessoryCross="isDispAccessoryCross"
      :accessoryImg="dispAccessoryImg"
    />
    <!-- 扉開閉線 -->
    <dd-door-open-line
      v-for="doorOpenLine in doorOpenLineMap"
      :key="'door-' + doorOpenLine.space.id"
      :space="doorOpenLine.space"
      :attributeValue1="doorOpenLine.att ? doorOpenLine.att.attValue1 : undefined"
      :viewType="viewSet.viewType"
      :sectionPositionZ="convertedSectionPosition.z"
      :scaleStrokeWidth="scaleStrokeWidth"
    />
    <!-- 隠れた空間 -->
    <g id="hidden-space-lines" fill="none" :stroke="hiddenSpaceLine.color">
      <g
        v-for="hiddenSpace in hiddenSpaces"
        :key="'hidden-space-' + hiddenSpace.id"
      >
        <!-- 隠れた空間（隠れた加工付き空間パスが存在する場合は非表示） -->
        <rect
          v-if="hiddenSpacePath(hiddenSpace) == null || hiddenSpacePath(hiddenSpace) === ''"
          :x="hiddenSpace.x"
          :y="hiddenSpace.y"
          :width="hiddenSpace.w"
          :height="hiddenSpace.h"
          :stroke-width="scaleStrokeWidth(hiddenSpaceLine.width)"
          :stroke-dasharray="getDashArray(hiddenSpaceLine.dasharray)"
        />
        <!-- 隠れた加工付き空間パス -->
        <path
          v-else
          :d="hiddenSpacePath(hiddenSpace)"
          :transform="'translate(' + hiddenSpace.x + ' ' + hiddenSpace.y + ')'"
          :stroke-width="scaleStrokeWidth(hiddenSpaceLine.width)"
          :stroke-dasharray="getDashArray(hiddenSpaceLine.dasharray)"
          :data-front-position="hiddenSpace.z + hiddenSpace.d"
          :data-parent-id="hiddenSpace.parentSpaceId"
        />
      </g>
    </g>
  </g>
</template>

<script>
import { SVG_LINE } from '../../../constants/App'
import * as App from '../../../utils/App'
import {PLANNING_TYPE, ATTRIBUTE_KEY, BOARD_DIR_TYPE, GRAIN_SETTING_TYPE, GRAIN_DIRECTION_TYPE, ELEMENT_FACE_TYPE, OBJECT_TYPE, VIEW_TYPE} from '../../../constants/Type'
import DispSpace from './DispSpace'
import DoorOpenLine from './DoorOpenLine'

export default {
  name: 'Space',
  components: {
    'dd-disp-space': DispSpace,
    'dd-door-open-line': DoorOpenLine
  },
  props: {
    /** 対象空間 */
    space: Object,
    /** 追加対象空間データ */
    addTargetSpace: Object,
    /** 編集対象親セル空間ID */
    editTargetCellSpaceParentId: Number,
    /** ビューの種別情報の定義情報 */
    viewSet: Object, // Type.VIEW_SET
    /** 加工線表示 */
    isDispProcess: Boolean,
    /** 縮尺に応じた線の太さを返す */
    scaleStrokeWidth: Function,
    /** 可動棚線を表示するか */
    showInfillLine: Function,
    /** 空間ごとの扉開閉線 */
    doorOpenLineMap: Array,
    /** ビューの向きに応じた断面線位置 */
    convertedSectionPosition: Object,
    /** 空間ごとの隠れた空間リスト */
    hiddenSpaces: Array,
    getDashArray: Function,
    /** ファイルデータ */
    fileData: Object,
    /** 空間ごとのオブジェクトデータモデル */
    objectDataModel: Object,
    /** アクセサリー表示 */
    isDispAccessory: Boolean,
    /** 編集中加工ID */
    focusedProcessId: Number
  },
  data () {
    return {
      hiddenSpaceLine: SVG_LINE.HIDDEN_SPACE,
      colorDispTexSize: {},
      dispTexName: '',
      colorId: ''
    }
  },
  computed: {
    /** 隠れた加工付き空間パス */
    hiddenSpacePath () {
      return function ({ pathDMap }) {
        const { code: viewTypeCode } = this.viewSet.viewType
        if (Object.keys(pathDMap).length && pathDMap[viewTypeCode] !== undefined) {
          return pathDMap[viewTypeCode]
        }
        return ''
      }
    },
    // *****************************************************************
    // DispSpace.vueに受け渡すデータ
    // *****************************************************************
    colorDispTex () {
      const { planningType } = this.space
      if (planningType === PLANNING_TYPE.ELEMENT) {
        if (this.objectDataModel.objElStyleColorId != null || this.objectDataModel.objElMaterialId !== this.colorId) {
          return this.setDispTex()
        } else {
          return this.dispTexName
        }
      }
    },
    /**
     * 木目方向を取得する
     * 0：木目なし、1：縦目、2：横目
     * @returns {string}
     */
    drawingGrain () {
      const { parentSpaceId } = this.space
      // 配列方向
      const flgDir = this.fileData.attributeModelList.find(att => {
        return ATTRIBUTE_KEY.PT_FLAG_DIR === att.attKeycd && att.spaceId === parentSpaceId
      })
      if (flgDir == null) return GRAIN_SETTING_TYPE.TYPE_GRAIN_NONE
      const flgDirValue1 = flgDir.attValue1
      if (flgDirValue1 == null) return GRAIN_SETTING_TYPE.TYPE_GRAIN_NONE

      // 木目方向情報
      const objectData = this.objectDataModel
      if (objectData == null) return GRAIN_SETTING_TYPE.TYPE_GRAIN_NONE
      const objElGrain = objectData.objElGrain
      if (objElGrain == null || objElGrain === 9) return GRAIN_SETTING_TYPE.TYPE_GRAIN_NONE

      // 木目方向
      let grain = GRAIN_SETTING_TYPE.TYPE_GRAIN_NONE
      switch (flgDirValue1) {
        case BOARD_DIR_TYPE.TYPE_BOARD_DIR_1:
          grain = GRAIN_SETTING_TYPE.TYPE_GRAIN_HORIZON
          break
        case BOARD_DIR_TYPE.TYPE_BOARD_DIR_2:
          grain = GRAIN_SETTING_TYPE.TYPE_GRAIN_VERTICAL
          break
        case BOARD_DIR_TYPE.TYPE_BOARD_DIR_3:
          // 取得面タイプが上面か下面の場合横目。そうでないなら縦目
          grain = [ELEMENT_FACE_TYPE.TOP.code, ELEMENT_FACE_TYPE.BOTTOM.code].includes(objectData.objElFaceType)
            ? GRAIN_SETTING_TYPE.TYPE_GRAIN_HORIZON
            : GRAIN_SETTING_TYPE.TYPE_GRAIN_VERTICAL
          break
        default:
          break
      }

      // 反対方向で設定されていたら反対にする
      if (objElGrain === GRAIN_DIRECTION_TYPE.GRAIN_REVERSE_DIRECTION) {
        grain = grain === GRAIN_SETTING_TYPE.TYPE_GRAIN_VERTICAL
          ? GRAIN_SETTING_TYPE.TYPE_GRAIN_HORIZON
          : grain === GRAIN_SETTING_TYPE.TYPE_GRAIN_HORIZON
            ? GRAIN_SETTING_TYPE.TYPE_GRAIN_VERTICAL
            : GRAIN_SETTING_TYPE.TYPE_GRAIN_NONE
      }

      return grain
    },
    spacePath () {
      const { pathDMap } = this.space
      const { code: viewTypeCode } = this.viewSet.viewType
      if (Object.keys(pathDMap).length && pathDMap[viewTypeCode] !== undefined) {
        return pathDMap[viewTypeCode]
      }
      return ''
    },
    dxfFace () {
      const { dxfFaceList } = this.space
      const { code: viewTypeCode } = this.viewSet.viewType
      if (dxfFaceList.length) {
        return dxfFaceList.find(dxfFace => dxfFace.faceType === viewTypeCode) || {}
      }
      return {}
    },
    strokeWidth () {
      const { addTargetSpaceId, editTargetCellSpaceParentId, scaleStrokeWidth, sectionPositionZ } = this
      const { id: spaceId, parentSpaceId, spaceObjectType, d: spaceD, z: spaceZ } = this.space
      // 種別による制御
      if (addTargetSpaceId === spaceId) {
        return scaleStrokeWidth(SVG_LINE.SELECT_CELL.width)
      }
      if (editTargetCellSpaceParentId === parentSpaceId &&
        spaceObjectType === OBJECT_TYPE.CELL.code) {
        return scaleStrokeWidth(SVG_LINE.SELECT_CELL.width)
      }
      switch (spaceObjectType) {
        case OBJECT_TYPE.ELEMENT.code: return scaleStrokeWidth(SVG_LINE.ELEMENT.width)
        case OBJECT_TYPE.CELL.code: return scaleStrokeWidth(SVG_LINE.CELL.width)
        case OBJECT_TYPE.COMBINE.code: return scaleStrokeWidth(SVG_LINE.COMBINE.width)
        case OBJECT_TYPE.ROOM.code: return scaleStrokeWidth(SVG_LINE.ROOM.width)
        case OBJECT_TYPE.SETPLAN.code:
        case OBJECT_TYPE.SETPLAN_FIX.code:
          return scaleStrokeWidth(SVG_LINE.SETPLAN.width)
      }
      // 断面線
      if (spaceZ <= sectionPositionZ && sectionPositionZ <= spaceZ + spaceD) {
        return scaleStrokeWidth(SVG_LINE.SPACE_SECTION.width)
      }
      switch (spaceObjectType) {
        case OBJECT_TYPE.UNITCOMPONENT.code: return scaleStrokeWidth(SVG_LINE.UNITCOMPONENT.width)
        case OBJECT_TYPE.UNIT.code:
          if (PLANNING_TYPE.ACCESSORY === this.space.planningType) {
            return this.scaleStrokeWidth(SVG_LINE.ACCESSORY.width)
          }
          return this.scaleStrokeWidth(SVG_LINE.UNIT.width)
        case OBJECT_TYPE.PARTS.code: return scaleStrokeWidth(SVG_LINE.PARTS.width)
        case OBJECT_TYPE.FITTING.code: return scaleStrokeWidth(SVG_LINE.FITTING.width)
      }
      return scaleStrokeWidth(SVG_LINE.DEFAULT.width)
    },
    ownProcessList () {
      const { processFixList } = this.fileData
      if (!processFixList || !processFixList.length) {
        return []
      }

      const { setBoringFace } = this
      const { viewType, rotate: viewRotate } = this.viewSet
      const { id: spaceId, x: spaceX, y: spaceY } = this.space
      return processFixList.filter(e => e.pdOwnSpId === spaceId).map(e => {
        let converted = { ...e, pdRx: 0, pdRy: 0, focusFlg: e.pdId === this.focusedProcessId }

        converted = setBoringFace(converted)

        switch (viewType) {
          case VIEW_TYPE.FRONT:
          case VIEW_TYPE.BACK:
            break
          case VIEW_TYPE.TOP:
          case VIEW_TYPE.BOTTOM:
            converted = {
              ...converted,
              pdY: e.pdZ,
              pdZ: e.pdY,
              pdH: e.pdD,
              pdD: e.pdH
            }
            break
          case VIEW_TYPE.RIGHT:
          case VIEW_TYPE.LEFT:
            converted = {
              ...converted,
              pdX: e.pdZ,
              pdZ: e.pdX,
              pdW: e.pdD,
              pdD: e.pdW
            }
            break
          default:
            break
        }

        let rotated = { ...converted }
        if (!isNaN(viewRotate) && viewRotate % 180 !== 0) {
          rotated = {
            ...rotated,
            pdX: e.pdY,
            pdY: e.pdX,
            pdW: e.pdH,
            pdH: e.pdW
          }
        }
        return {
          ...rotated,
          x: spaceX + rotated.pdX - rotated.pdW / 2,
          y: spaceY + rotated.pdY - rotated.pdH / 2
        }
      })
    },
    pathSize () {
      const { w: spaceW, h: spaceH } = this.space
      const { fitType, width: dxfFaceWidth, height: dxfFaceHeight } = this.dxfFace
      const spaceWidthBasedOnHeight = spaceH * (dxfFaceWidth / dxfFaceHeight)
      const spaceHeightBasedOnWidth = spaceW * (dxfFaceHeight / dxfFaceWidth)
      switch (fitType) {
        case 'NONE':
          return {
            w: dxfFaceWidth,
            h: dxfFaceHeight
          }
        case 'VERTICAL':
          return {
            w: spaceWidthBasedOnHeight,
            h: spaceH
          }
        case 'HORIZONTAL':
          return {
            w: spaceW,
            h: spaceHeightBasedOnWidth
          }
        case 'INSIDE':
          if (spaceHeightBasedOnWidth > spaceH) {
            return {
              w: spaceWidthBasedOnHeight,
              h: spaceH
            }
          }
          return {
            w: spaceW,
            h: spaceHeightBasedOnWidth
          }
        case 'FULL':
        default:
          return {
            w: spaceW,
            h: spaceH
          }
      }
    },
    isDispAccessoryCross () {
      return this.isDispAccessory && PLANNING_TYPE.ACCESSORY === this.space.planningType
    },
    dispAccessoryImg () {
      return this.setAccessoryImg()
    }
  },
  methods: {
    // *****************************************************************
    // DispSpace.vueに受け渡すデータを用意する処理
    // *****************************************************************
    setDispTex: function () {
      this.colorId = this.objectDataModel.objElMaterialId
      try {
        const { materialMasterList, colorMasterList } = this.fileData
        // カラーマスターを取得
        let colorMaster = null
        if (this.objectDataModel.objElStyleColorId != null) {
          colorMaster = colorMasterList.find(color => color.colorId === this.objectDataModel.objElStyleColorId)
        } else {
          const materialMaster = materialMasterList.find(material => material.materialId === this.objectDataModel.objElMaterialId)
          colorMaster = colorMasterList.find(color => color.colorId === materialMaster.materialBaseColorId)
        }
        // カラーマスターに設定されている画面用テクスチャを返す
        if (colorMaster) {
          const { colorDispTexVName, colorDispTexHName, colorDispTexVX, colorDispTexHX, colorDispTexVY, colorDispTexHY } = colorMaster
          switch (this.drawingGrain) {
            case GRAIN_SETTING_TYPE.TYPE_GRAIN_VERTICAL:
            case GRAIN_SETTING_TYPE.TYPE_GRAIN_NONE:
              if (colorDispTexVName && colorDispTexVX && colorDispTexVY) {
                this.setColorDispTexSize(colorDispTexVX, colorDispTexVY)
                this.dispTexName = this.getImage('tex/' + colorDispTexVName)
                return this.dispTexName
              }
              break
            case GRAIN_SETTING_TYPE.TYPE_GRAIN_HORIZON:
              if (colorDispTexHName && colorDispTexHX && colorDispTexHY) {
                this.setColorDispTexSize(colorDispTexHX, colorDispTexHY)
                this.dispTexName = this.getImage('tex/' + colorDispTexHName)
                return this.dispTexName
              }
              break
            default:
              break
          }
        }
      } catch (e) {}
      return ''
    },
    setBoringFace: function (process) {
      const { pdType, pdBaseFaceType, pdW, pdH } = process
      const { viewType } = this.viewSet
      if (pdType === 'TYPE_PR_BORING') {
        switch (VIEW_TYPE[pdBaseFaceType]) {
          case VIEW_TYPE.BACK:
          case VIEW_TYPE.FRONT:
            switch (viewType) {
              case VIEW_TYPE.BACK:
              case VIEW_TYPE.FRONT:
                return {
                  ...process,
                  pdRx: pdW / 2,
                  pdRy: pdH / 2
                }
            }
            break

          case VIEW_TYPE.LEFT:
          case VIEW_TYPE.RIGHT:
            switch (viewType) {
              case VIEW_TYPE.LEFT:
              case VIEW_TYPE.RIGHT:
                return {
                  ...process,
                  pdRx: pdW / 2,
                  pdRy: pdH / 2
                }
            }
            break

          case VIEW_TYPE.BOTTOM:
          case VIEW_TYPE.TOP:
            switch (viewType) {
              case VIEW_TYPE.BOTTOM:
              case VIEW_TYPE.TOP:
                return {
                  ...process,
                  pdRx: pdW / 2,
                  pdRy: pdH / 2
                }
            }
            break
        }
      }
      return process
    },
    setColorDispTexSize (colorDispTexVX, colorDispTexVY) {
      this.colorDispTexSize = {
        width: colorDispTexVX,
        height: colorDispTexVY
      }
    },
    setAccessoryImg () {
      if (!this.space || this.space.length === 0) return ''
      let user = App.getUserInfo()
      if (user && user.corpId === 170) {
        let accessoryAttr = this.fileData.attributeModelList.find(att => ATTRIBUTE_KEY.UT_ACCESSORY_INFO === att.attKeycd && att.spaceId === this.space.id)
        if (accessoryAttr && accessoryAttr.attString1.length !== 0) {
          return this.getImage('accessory/' + accessoryAttr.attString1 + '.png')
        }
        return ''
      }
      let objectData = this.fileData.objectDataModelList.find(e => e.spaceId === this.space.id)
      return this.getImage('accessory/' + objectData.umCode + '.png')
    },
    getImage (image) {
      return App.getDarosResource(image)
    }
  },
  mounted () {}
}
</script>



// WEBPACK FOOTER //
// src/main/js/components/planning/parts/Space.vue